/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The dashboards list.
 *
 * Deliberately simpler than ReportsHome: there are usually a handful of
 * dashboards against dozens of reports, so a folder rail would be more chrome
 * than content. Folders still exist on the backend and a dashboard can live in
 * one; this page just does not lead with them.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, Menu, MenuItem, Snackbar, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded, AutoAwesome, ClearRounded, ContentCopyRounded, DashboardCustomizeRounded,
  DeleteOutlineRounded, HomeRounded, MoreVertRounded, SearchRounded,
  ShareOutlined, WidgetsRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import CreateWithAiDialog from 'components/AiAssistant/CreateWithAiDialog';
import { useAiAssistant } from 'context/AiAssistantContext';
import ShareDialog from './ShareDialog';

export default function DashboardsList() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { enabled: aiEnabled } = useAiAssistant();
  const [aiOpen, setAiOpen] = useState(false);

  const [dashboards, setDashboards] = useState([]);
  const [homeId, setHomeId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, dashboard: null });
  const [sharing, setSharing] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, home] = await Promise.all([
        axiosInstance.get('/analytics/dashboards', { params: { limit: 200 } }),
        axiosInstance.get('/analytics/home-dashboard').catch(() => ({ data: {} })),
      ]);
      setDashboards(Array.isArray(list.data) ? list.data : []);
      setHomeId(home.data?.dashboard_id ?? null);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 403
        ? 'You do not have permission to view dashboards.'
        : 'Dashboards could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return dashboards;
    return dashboards.filter((d) =>
      `${d.name} ${d.description ?? ''}`.toLowerCase().includes(term));
  }, [dashboards, search]);

  const create = async () => {
    const name = (creating ?? '').trim();
    if (!name) return;
    try {
      const { data } = await axiosInstance.post('/analytics/dashboards', {
        name, layout: {}, filter_config: {}, widgets: [],
      });
      setCreating(null);
      navigate(`/dashboards/${data.id}`);
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be created.') });
    }
  };

  const remove = async (dashboard) => {
    try {
      await axiosInstance.delete(`/analytics/dashboards/${dashboard.id}`);
      setToast({ severity: 'success', message: 'Dashboard deleted.' });
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be deleted.') });
    }
  };

  const clone = async (dashboard) => {
    try {
      const { data } = await axiosInstance.post(
        `/analytics/dashboards/${dashboard.id}/clone`, {});
      navigate(`/dashboards/${data.id}`);
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be copied.') });
    }
  };

  const setHome = async (dashboard) => {
    try {
      await axiosInstance.put('/analytics/home-dashboard', { dashboard_id: dashboard.id });
      setHomeId(dashboard.id);
      setToast({ severity: 'success', message: `“${dashboard.name}” is now your home page.` });
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be saved.') });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between"
             gap={2} mb={3} flexWrap="wrap">
          <Box>
            <Typography variant="h4" fontWeight={700} lineHeight={1.2}>Dashboards</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Arrange charts from your reports, and pin one as your home page.
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {aiEnabled && (
              <MDButton variant="outlined" color="info" onClick={() => setAiOpen(true)}>
                <AutoAwesome sx={{ mr: 0.75 }} fontSize="small" /> Create with AI
              </MDButton>
            )}
            <MDButton variant="gradient" color="info" onClick={() => setCreating('')}>
              <AddRounded sx={{ mr: 0.75 }} /> New dashboard
            </MDButton>
          </Box>
        </Box>
        <CreateWithAiDialog open={aiOpen} kind="dashboard" onClose={() => setAiOpen(false)} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          size="small" fullWidth placeholder="Search dashboards…" sx={{ mb: 2, maxWidth: 420 }}
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !visible.length ? (
          <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed',
                     borderColor: 'divider', borderRadius: '14px' }}>
            <DashboardCustomizeRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {search ? 'Nothing matches that search.' : 'No dashboards yet'}
            </Typography>
            {!search && (
              <>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Build a report, chart it, and drop that chart here.
                </Typography>
                <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                  {aiEnabled && (
                    <MDButton variant="outlined" color="info" onClick={() => setAiOpen(true)}>
                      <AutoAwesome sx={{ mr: 0.75 }} fontSize="small" /> Create with AI
                    </MDButton>
                  )}
                  <MDButton variant="gradient" color="info" onClick={() => setCreating('')}>
                    <AddRounded sx={{ mr: 0.75 }} /> New dashboard
                  </MDButton>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box display="grid" gap={2}
               gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}>
            {visible.map((dashboard) => (
              <Box
                key={dashboard.id}
                onClick={() => navigate(`/dashboards/${dashboard.id}`)}
                sx={{
                  p: 2, cursor: 'pointer', borderRadius: '14px',
                  border: '1px solid', borderColor: 'divider',
                  backgroundColor: colors.background.white,
                  transition: 'border-color .15s, box-shadow .15s',
                  '&:hover': { borderColor: colors.brand.primary,
                               boxShadow: '0 2px 10px rgba(21,101,200,.08)' },
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
                             display: 'grid', placeItems: 'center',
                             backgroundColor: colors.brand.primaryContainer,
                             color: colors.brand.primary }}>
                    <DashboardCustomizeRounded sx={{ fontSize: 18 }} />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {dashboard.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {dashboard.description || 'No description'}
                    </Typography>
                  </Box>
                  <IconButton size="small"
                    onClick={(e) => { e.stopPropagation();
                                      setMenu({ anchor: e.currentTarget, dashboard }); }}>
                    <MoreVertRounded fontSize="small" />
                  </IconButton>
                </Box>

                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  <Chip
                    size="small" icon={<WidgetsRounded sx={{ fontSize: 13 }} />}
                    label={`${dashboard.widgets?.length ?? 0} widget${
                      (dashboard.widgets?.length ?? 0) === 1 ? '' : 's'}`}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                  {homeId === dashboard.id && (
                    <Tooltip title="Your home page">
                      <Chip
                        size="small" icon={<HomeRounded sx={{ fontSize: 13 }} />} label="Home"
                        sx={{ height: 20, fontSize: 11, fontWeight: 700,
                              backgroundColor: colors.status.success.background,
                              color: colors.status.success.text }}
                      />
                    </Tooltip>
                  )}
                  {dashboard.is_template && (
                    <Chip size="small" label="Built-in"
                          sx={{ height: 20, fontSize: 11, fontWeight: 700,
                                backgroundColor: colors.status.info.background,
                                color: colors.status.info.text }} />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </MDBox>

      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)}
            onClose={() => setMenu({ anchor: null, dashboard: null })}>
        <MenuItem onClick={() => { setHome(menu.dashboard); setMenu({ anchor: null }); }}>
          <HomeRounded fontSize="small" sx={{ mr: 1 }} /> Set as my home page
        </MenuItem>
        <MenuItem onClick={() => { clone(menu.dashboard); setMenu({ anchor: null }); }}>
          <ContentCopyRounded fontSize="small" sx={{ mr: 1 }} /> Make a copy
        </MenuItem>
        {menu.dashboard?.access === 'manage' && (
          <MenuItem onClick={() => { setSharing(menu.dashboard); setMenu({ anchor: null }); }}>
            <ShareOutlined fontSize="small" sx={{ mr: 1 }} /> Share
          </MenuItem>
        )}
        {menu.dashboard?.access === 'manage' && !menu.dashboard?.is_template && (
          <MenuItem onClick={() => { remove(menu.dashboard); setMenu({ anchor: null }); }}
                    sx={{ color: colors.error.text }}>
            <DeleteOutlineRounded fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <ShareDialog open={Boolean(sharing)} onClose={() => setSharing(null)}
                   resourceType="dashboard" resource={sharing} />

      <Dialog open={creating !== null} onClose={() => setCreating(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>New dashboard</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Name" sx={{ mt: 1 }}
                     value={creating ?? ''} onChange={(e) => setCreating(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && create()} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setCreating(null)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={create}
                    disabled={!(creating ?? '').trim()}>Create</MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
